import moment from "moment";

import { deliveryCategory, purchaseDetailCategory } from "SCConstants";

export const toQuantity = (value: any) =>
  parseFloat(parseFloat(value || 0).toFixed(3));

// Stock events store the measure unit as its id (see IncidentEdit)
const toMeasureUnitId = (measureUnit: any) =>
  measureUnit && typeof measureUnit === "object" ? measureUnit.id : measureUnit;

const buildIncidentStockEvent = (
  base: {
    commodity: number;
    category: string;
    measureUnit?: any;
  },
  quantity: number,
  related: {
    relatedEventId?: number;
    relatedEventType: "DEL" | "PUR";
    batch_no?: string;
  }
) => ({
  commodity: base.commodity,
  category: base.category,
  ...(base.measureUnit !== undefined
    ? { measure_unit: toMeasureUnitId(base.measureUnit) }
    : {}),
  quantity: toQuantity(quantity),
  sign: -1,
  type: "INC",
  target: related.relatedEventType,
  issues: [],
  ...(related.batch_no !== undefined ? { batch_no: related.batch_no } : {}),
  side_effects:
    related.relatedEventId !== undefined
      ? [
          {
            id: 0,
            related_event_id: related.relatedEventId,
            related_event_type: related.relatedEventType,
            chain_direction: "affects" as const,
            qty_requested: toQuantity(quantity),
            qty_fulfilled: toQuantity(quantity),
            partial: false,
          },
        ]
      : undefined,
  meta: {
    is_carry_over: true,
    category: base.category,
    ...(related.batch_no !== undefined ? { batch_no: related.batch_no } : {}),
  },
});

/**
 * Builds the incident stock events covering `quantity` of a commodity's
 * carry-over delivery stock, consuming the source batches FIFO
 * (earliest delivery first).
 */
export const buildCarryOverDeliveryIncidentEvents = (
  carryOverStockObject: any,
  commodity: number,
  quantity: number
) => {
  const quantityPerBatchNo = carryOverStockObject?.quantityPerBatchNo || {};
  const batches = Object.keys(quantityPerBatchNo)
    .flatMap((batch_no) =>
      (quantityPerBatchNo[batch_no]?.deliveryCommodityDetails || []).map(
        (detail: any) => ({
          batch_no,
          quantity: toQuantity(detail.quantity),
          date: detail.date,
          relatedEventId: detail.deliveryCommodityId,
        })
      )
    )
    .sort((a, b) => (moment(a.date).isAfter(b.date) ? 1 : -1));

  let remainingQuantity = toQuantity(quantity);
  const events: any[] = [];
  batches.forEach((batch) => {
    if (remainingQuantity <= 0) return;
    const quantityToTake = Math.min(remainingQuantity, batch.quantity);
    if (quantityToTake > 0) {
      events.push(
        buildIncidentStockEvent(
          {
            commodity,
            category: deliveryCategory,
            measureUnit: carryOverStockObject?.measure_unit,
          },
          quantityToTake,
          {
            relatedEventId: batch.relatedEventId,
            relatedEventType: "DEL",
            batch_no: batch.batch_no,
          }
        )
      );
      remainingQuantity = toQuantity(remainingQuantity - quantityToTake);
    }
  });
  return events;
};

/**
 * Builds the incident stock events covering `quantity` of a commodity's
 * carry-over purchase stock, consuming the source purchases FIFO
 * (earliest purchase first).
 */
export const buildCarryOverPurchaseIncidentEvents = (
  carryOverPurchaseStockObject: any,
  commodity: number,
  quantity: number
) => {
  const purchases = (carryOverPurchaseStockObject?.purchases || [])
    .map((purchase: any) => ({
      quantity: toQuantity(purchase.quantity),
      date: purchase.date,
      relatedEventId: purchase.purchaseCommodityId,
    }))
    .filter((purchase: any) => purchase.quantity > 0)
    .sort((a: any, b: any) => (moment(a.date).isAfter(b.date) ? 1 : -1));

  let remainingQuantity = toQuantity(quantity);
  const events: any[] = [];
  purchases.forEach((purchase: any) => {
    if (remainingQuantity <= 0) return;
    const quantityToTake = Math.min(remainingQuantity, purchase.quantity);
    if (quantityToTake > 0) {
      events.push(
        buildIncidentStockEvent(
          {
            commodity,
            category: purchaseDetailCategory,
            measureUnit: carryOverPurchaseStockObject?.measure_unit,
          },
          quantityToTake,
          {
            relatedEventId: purchase.relatedEventId,
            relatedEventType: "PUR",
          }
        )
      );
      remainingQuantity = toQuantity(remainingQuantity - quantityToTake);
    }
  });
  return events;
};

const groupKey = (cause: any, reason: any) =>
  `${parseInt(cause)}:${parseInt(reason)}`;

const normalizeEvents = (events: any[]) =>
  JSON.stringify(
    events.map((event) => [
      event.commodity,
      toQuantity(event.quantity),
      event.batch_no ?? null,
      event.side_effects?.[0]?.related_event_id ?? null,
    ])
  );

/**
 * Computes how the existing initial-stock incident stores must change so that
 * they exactly reflect the submitted form state ("the form wins"):
 *
 * - A row lowered below its adjusted quantity (a reason was selected) has ALL
 *   of its commodity's incidents replaced by fresh events covering the whole
 *   reduction (initial - input), under the newly selected cause/reason.
 * - A row raised back towards its initial quantity shrinks the existing
 *   events (most recently created first), keeping their original reason.
 * - An untouched row leaves its incidents unchanged.
 *
 * Incident stores hold one (cause, reason) pair each, shared across
 * commodities. Returns a plan of stores to create, update and remove.
 */
export const reconcileInitialStockIncidents = ({
  rows,
  initialQuantities,
  adjustedQuantities,
  existingIncidents,
  buildEventsForCommodity,
  occurredAt,
  category,
}: {
  rows: any[];
  initialQuantities: Record<string, number>;
  adjustedQuantities: Record<string, number>;
  existingIncidents: any[];
  buildEventsForCommodity: (commodity: number, quantity: number) => any[];
  occurredAt: string;
  category: string;
}) => {
  // Existing stock events per commodity, in store/event order, each tagged
  // with its store's (cause, reason) group
  const existingEventsByCommodity: Record<string, any[]> = {};
  existingIncidents.forEach((store) => {
    const key = groupKey(store.causes?.[0], store.meta?.reason);
    (store.stock_events || []).forEach((event: any) => {
      if (!existingEventsByCommodity[event.commodity]) {
        existingEventsByCommodity[event.commodity] = [];
      }
      existingEventsByCommodity[event.commodity].push({
        key,
        causes: store.causes,
        reason: store.meta?.reason,
        event,
      });
    });
  });

  const groups: Record<
    string,
    { causes: number[]; reason: number; events: any[] }
  > = {};
  const addToGroup = (key: string, causes: any, reason: any, event: any) => {
    if (!groups[key]) {
      groups[key] = {
        causes: (causes || []).map((cause: any) => parseInt(cause)),
        reason: parseInt(reason),
        events: [],
      };
    }
    groups[key].events.push(event);
  };

  (rows || []).forEach((row: any) => {
    const commodity = row?.commodity;
    if (!commodity || initialQuantities[commodity] === undefined) return;

    const initialQuantity = initialQuantities[commodity];
    const adjustedQuantity = adjustedQuantities[commodity] ?? initialQuantity;
    const input = Math.min(toQuantity(row.quantity), initialQuantity);
    const desiredReduction = toQuantity(initialQuantity - input);

    const existingEntries = existingEventsByCommodity[commodity] || [];
    delete existingEventsByCommodity[commodity];

    if (row.reason && input < adjustedQuantity) {
      // Override: replace the commodity's incidents with a fresh set of
      // events covering the whole reduction, under the new cause/reason
      const reason = parseInt(row.reason);
      const cause = parseInt(row.cause);
      buildEventsForCommodity(
        parseInt(commodity),
        desiredReduction
      ).forEach((event) =>
        addToGroup(groupKey(cause, reason), [cause], reason, event)
      );
    } else {
      // Keep, or shrink when the quantity was raised back up. Excess is
      // removed from the most recently created events first, so the
      // earliest-consumed source batches are restored last (FIFO undo)
      const existingTotal = toQuantity(
        existingEntries.reduce(
          (sum, entry) => sum + toQuantity(entry.event.quantity),
          0
        )
      );
      let excess = toQuantity(existingTotal - desiredReduction);
      const keptQuantities: number[] = new Array(existingEntries.length);
      for (let i = existingEntries.length - 1; i >= 0; i--) {
        const quantity = toQuantity(existingEntries[i].event.quantity);
        const quantityToTake = excess > 0 ? Math.min(quantity, excess) : 0;
        excess = toQuantity(excess - quantityToTake);
        keptQuantities[i] = toQuantity(quantity - quantityToTake);
      }
      existingEntries.forEach((entry, i) => {
        if (keptQuantities[i] > 0) {
          addToGroup(entry.key, entry.causes, entry.reason, {
            ...entry.event,
            quantity: keptQuantities[i],
          });
        }
      });
    }
  });

  // Commodities with incidents but no form row are left untouched
  Object.keys(existingEventsByCommodity).forEach((commodity) => {
    existingEventsByCommodity[commodity].forEach((entry) =>
      addToGroup(entry.key, entry.causes, entry.reason, entry.event)
    );
  });

  const toCreate: any[] = [];
  const toUpdate: { client_id: string; values: any }[] = [];
  const leftoverStores = [...existingIncidents];

  Object.keys(groups).forEach((key) => {
    const group = groups[key];
    if (group.events.length === 0) return;

    const values = {
      event_type: "incident",
      causes: group.causes,
      stock_events: group.events,
      occurred_at: occurredAt,
      category,
      meta: {
        reason: group.reason,
        other_cause: "",
        is_initial_stock_incident: true,
      },
    };

    const storeIndex = leftoverStores.findIndex(
      (store) => groupKey(store.causes?.[0], store.meta?.reason) === key
    );
    if (storeIndex !== -1) {
      const store = leftoverStores.splice(storeIndex, 1)[0];
      // Unchanged stores need no dispatch, which keeps saving idempotent
      if (
        normalizeEvents(store.stock_events || []) ===
        normalizeEvents(group.events)
      ) {
        return;
      }
      toUpdate.push({
        client_id: store.client_id,
        values: {
          ...values,
          meta: { ...(store.meta || {}), ...values.meta },
        },
      });
    } else {
      toCreate.push(values);
    }
  });

  return { toCreate, toUpdate, toRemove: leftoverStores };
};
